import { useCallback, useId, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { Button } from '@aimsos/ui';

import { isConciergeTask, TASK_GROUP_PREVIEW } from '../../constants/tasks.js';
import TaskCard from './TaskCard.jsx';
import TaskConciergeCard from './TaskConciergeCard.jsx';

import { ChevronDown, ChevronUp } from 'lucide-react';

/**
 * One group of the Tasks tab — Act now, This week, Later (CT-5722): a heading,
 * the first rows, and "Show N more" / "Show less" (local view state, with
 * `aria-expanded`). An empty group is not drawn.
 *
 * A row whose action goes to the Concierge is a `TaskConciergeCard`, which hands
 * off by itself (`entity` is the record); every other row dispatches through
 * `onAction`.
 */
function TaskGroup({ groupKey, items, entity, actionStateOf, isRunning, onAction, onMenu }) {
  const { t } = useTranslation('unified-profiles');
  const headingId = useId();
  const [expanded, setExpanded] = useState(false);
  const toggle = useCallback(() => setExpanded((value) => !value), []);

  if (!items.length) return null;

  const visible = expanded ? items : items.slice(0, TASK_GROUP_PREVIEW);
  const hidden = items.length - TASK_GROUP_PREVIEW;

  return (
    <section aria-labelledby={headingId} data-testid={`task-group-${groupKey}`} className="flex flex-col gap-3">
      <h3 id={headingId} className="text-xs font-semibold uppercase tracking-wide text-content-muted">
        {t(`tasks.groups.${groupKey}`)}
      </h3>
      <div className="flex flex-col gap-3">
        {visible.map((task) =>
          isConciergeTask(task) ? (
            <TaskConciergeCard
              key={task.id}
              task={task}
              entity={entity}
              busy={isRunning(task.id)}
              onMenu={onMenu}
            />
          ) : (
            <TaskCard
              key={task.id}
              task={task}
              actionState={actionStateOf(task)}
              busy={isRunning(task.id)}
              onAction={onAction}
              onMenu={onMenu}
            />
          ),
        )}
      </div>
      {hidden > 0 ? (
        <div>
          <Button
            variant="ghost"
            size="sm"
            rightIcon={expanded ? ChevronUp : ChevronDown}
            aria-expanded={expanded}
            onClick={toggle}
          >
            {expanded ? t('tasks.showLess') : t('tasks.showMore', { count: hidden })}
          </Button>
        </div>
      ) : null}
    </section>
  );
}

export default TaskGroup;
